import React, { useState, useRef, useCallback } from 'react';
import { throttle, noop } from 'lodash';
import { bool, func, object } from 'prop-types';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Icon } from '@fiverr-private/fit';
import { s_fullscreen } from '@fiverr-private/icons';
import Image from '../../Image';
import PinchToZoom from '../../PinchToZoom';

const ImageSlide = ({
    isActive = false,
    slide,
    onClick = noop,
    expanded = false,
    onZoomActive = noop,
    onZoomIdle = noop,
    preload = false,
    reportAction = noop,
}) => {
    const [isLongImage, setIsLongImage] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);
    const [hasBeenScrolled, setHasBeenScrolled] = useState(false);

    const wrapperNode = useRef();

    const updateScrollStatus = useCallback(() => {
        if (wrapperNode.current && !hasBeenScrolled) {
            const { scrollHeight, offsetHeight, scrollTop } = wrapperNode.current;
            setIsScrollable(scrollHeight > offsetHeight);
            setHasBeenScrolled(scrollTop > 5);
        }
    }, [hasBeenScrolled]);

    const throttledUpdateScrollStatus = useCallback(() => {
        throttle(updateScrollStatus, 250);
    }, [updateScrollStatus]);

    const handleDimensions = useCallback(
        ({ width, height }) => {
            // We consider an image with an aspect ratio of 1:1.5 or taller a "Long image";
            const longAspectRatio = 1 / 1.5;
            const imageAspectRatio = width / height;
            if (imageAspectRatio <= longAspectRatio) {
                setIsLongImage(true);
                throttledUpdateScrollStatus();
            }
        },
        [throttledUpdateScrollStatus]
    );

    const onClickWrapper = () => {
        onClick();
        !expanded && reportAction();
    };

    const { name, media } = slide;
    const figureClasses = classNames('thumbnail', { 'long-image': isLongImage });
    const slideClasses = classNames('slide', 'slide-image', {
        'scroll-hint': isScrollable && !hasBeenScrolled,
    });
    const lazyLoad = !isActive && !preload;
    const src = expanded ? media.original : media.medium;

    const thumbnail = (
        <figure className={figureClasses} tabIndex="0" ref={wrapperNode} onScroll={throttledUpdateScrollStatus}>
            <Image lazyLoad={lazyLoad} isActive={isActive} alt={name} src={src} onDimensions={handleDimensions} />
        </figure>
    );

    return (
        <div className={slideClasses} onClick={onClickWrapper}>
            {expanded ? (
                <PinchToZoom
                    rotate={false}
                    minScale={1.5}
                    onActive={onZoomActive}
                    onIdle={onZoomIdle}
                    preventSingleTouchEvents={!isLongImage}
                >
                    {thumbnail}
                </PinchToZoom>
            ) : (
                thumbnail
            )}
            {!expanded && (
                <div className="slide-fullscreen">
                    <Icon size={13} className="fullscreen-icon">
                        {s_fullscreen}
                    </Icon>
                    <I18n k="assets_sharing.gallery.full_screen" />
                </div>
            )}
        </div>
    );
};

ImageSlide.propTypes = {
    isActive: bool,
    slide: object,
    onClick: func,
    expanded: bool,
    onZoomActive: func,
    onZoomIdle: func,
    preload: bool,
    reportAction: func,
};

export default ImageSlide;
